import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter, inject,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {tap} from 'rxjs/operators';
import {
    CdkDrag,
    CdkDragDrop,
    CdkDropList,
    DragDropModule,
    moveItemInArray,
    transferArrayItem
} from "@angular/cdk/drag-drop";
import {first, merge, Observable, of, switchMap} from 'rxjs';
import {SelectSearchApiService} from '../select-search-api.service';
import {deepCopy, isGuid, mapOrder, uniqueObjectList} from "../../../lib/utils";
import {SELECT_SEARCH_PLACEHOLDER, TOOLTIP_SHOW_DELAY} from "../../../shared/globals";
import {Account} from "../../../_models/account";
import {StringFunction, ValueFunction} from "../types";
import {COMMON_FORM_IMPORTS} from "../../../shared/import-groups/Common";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";
import {OptionListSearchComponent} from "../../option-list-search/option-list-search.component";
import {DivInfiniteScrollDirective} from "../../../directives/div-infinite-scroll.directive";
import {DblClickCopyDirective} from "../../../directives/dbl-click-copy.directive";
import {IDMap, ModelID, ModelName} from "../../../_typing/generic-types";
import {isInteger as _isInteger} from "lodash-es";
import {ApiService} from "../../../services/api/api.service";
import {CommonModule} from "@angular/common";
import {ClearConstantService} from "../../constant-field/clear-constant.service";

@Component({
    selector: 'select-many-search-api',
    templateUrl: 'select-many-search-api.component.html',
    styleUrls: ['select-many-search-api.component.scss', '../../search-api/search-api.component.less'],
    encapsulation: ViewEncapsulation.None,
    providers: [SelectSearchApiService],
    imports: [
        CommonModule,
        COMMON_FORM_IMPORTS,
        MatMenu,
        MatMenuTrigger,
        OptionListSearchComponent,
        DivInfiniteScrollDirective,
        CdkDropList,
        CdkDrag,
        DblClickCopyDirective
    ],
})
export class SelectManySearchApiComponent implements OnInit, AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);
    private _value: any[];

    @ViewChild('availableList') availableListBox: CdkDropList;
    @ViewChild('selectedList') selectedListBox: CdkDropList;
    @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;

    @Input() placeholder: string = 'Search';
    @Input() apiModel: string;
    @Input() disabled: boolean = false;
    /**Entire selection element group**/
    @Input() disable_items: boolean = false;
    /**Individual items**/
    @Input() openOnInit: boolean = false;
    @Input() classes: string = 'fill';
    @Input() label: string = '';
    @Input() allowAccountSelection: boolean = true;
    @Input() allowedAccountIds: ModelID[];
    @Input() userSorted: boolean;

    @Input() stringFunction: StringFunction;
    @Input() valueFunction: ValueFunction;
    @Input() removeItemDisabled: (item) => boolean;
    @Input() compareFunctionName;
    /**Default = 'compareById'**/
    @Input() noItemsAvailableMessage: string = "";
    /**The api call that actually fetches data on the Input() api_model, defined in parent**/
    @Input() fetchData: (index: Number, filterValue: String, items_selected?: any[], items_removed?: any[]) => Observable<any[]>

    /**The api call that fetches selected data, defined in parent**/
    @Input() fetchSelected: (index: Number, filterValue: String, items_selected?: any[], items_removed?: any[]) => Observable<any[]>;
    @Input() required: boolean = false;

    /**
     * Preselect items in the data list.
     * @param value
     */
    @Input()
    set value(value: any[]) {
        this._value = value;
        if (this.apiModel) {
            this.setSelectTriggerText();
        }
    }

    get value(): any[] {
        return this._value;
    }

    /**
     * For running a function when the selection was changed.
     */
    @Output() selectChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() selectChangeComplete: EventEmitter<any> = new EventEmitter<any>();
    @Output() selectOrderChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() closeWithoutChange: EventEmitter<any> = new EventEmitter();
    @Output() itemsAdded: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemsRemoved: EventEmitter<any> = new EventEmitter();
    @Output() accountsChange = new EventEmitter<Account[]>();

    items_added: any[] = [];
    items_removed: any[] = [];
    $options_available: Observable<{ text: string, value: any }[]>;
    $options_selected: Observable<{ text: string, value: any }[]>;
    selectedItems: { text: string, value: any }[] = [];
    availableItems: { text: string, value: any }[] = [];
    isSelecting = false;
    isRemoving = false;

    initialised: boolean = false;
    tooltipShowDelay = TOOLTIP_SHOW_DELAY;
    searchText = "";
    idNameMap: IDMap<ModelName> = {};
    selectedTriggerText: string;
    placeholderText: string = SELECT_SEARCH_PLACEHOLDER;
    private clearValueService: ClearConstantService = inject(ClearConstantService);

    constructor(public sapi: SelectSearchApiService,
                private changeDetection: ChangeDetectorRef,
                private api: ApiService) {
        merge(
            this.clearValueService.clearSelectedRequested,
            this.clearValueService.clearInternalSelectedRequested,
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.clearPending());
    }

    ngOnInit(): void {
        this.setupItemFunctions();
    }

    ngAfterViewInit() {
        if (this.openOnInit) {
            this.menuTrigger.openMenu();
        }
        this.setSelectTriggerText();
    }

    menuOpened(): void {
        if (!this.initialised) {
            this.initialised = true;

            this.$options_available = this.sapi.connect();
            this.$options_available.pipe(
                switchMap(list => of(list)),
                tap(list => {
                    this.availableItems = uniqueObjectList(list, 'value');
                })
            ).subscribe(() => this.changeDetection.markForCheck());

            this.$options_selected = this.sapi.connect_selected();
            this.$options_selected.pipe(
                switchMap(list => of(list)),
                tap(list => {
                    this.selectedItems = uniqueObjectList(list, 'value');
                    if (this.userSorted) {
                        const order = this.value?.map(item => item.id || item);
                        this.selectedItems = mapOrder(this.selectedItems, order, 'value.id', true)
                    }
                })
            ).subscribe(() => this.changeDetection.markForCheck());

        }
        this.clearSearch();
    }

    clearPending() {
        this.items_added = [];
        this.items_removed = [];
        this.updateRemovedSelected();
        this.changeDetection.markForCheck();
    }

    filterOptions(filterString: string) {
        this.searchText = filterString;
        this.sapi.resetList([]);
        this.sapi.resetSelectedList();
        this.getNextBatch();
    }

    menuClosed(): void {
        this.selectChangeComplete.emit(this.value);
        this.sapi.resetList([]);
        this.sapi.resetSelectedList();
    }

    setupItemFunctions(): void {
        /**If value is function is passed in**/
        if (this.stringFunction) {
            this.sapi.stringFunction = this.stringFunction;
        }
        if (this.valueFunction) {
            this.sapi.valueFunction = this.valueFunction;
        }
    }

    updateSearchSelected(): void {
        this.sapi.resetSelectedList();
        this.getNextSelected(1);
    }

    getNextBatch(page?) {
        this.getNextAvailable(page);
        this.getNextSelected(page);
    }

    getNextAvailable(page?): void {
        this.sapi.getNextBatch(this.fetchData, this.searchText, page, this.items_added, this.items_removed);
    }

    getNextSelected(page?): void {
        this.sapi.getNextSelected(this.fetchSelected, this.searchText, page, this.items_added, this.items_removed);
    }

    drop(event: CdkDragDrop<{ text: string, value: any }[]>): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            this.value = event.container.data.map(item => item.value);
            this.selectOrderChange.emit(this.value);
        } else {
            const item = event.previousContainer.data[event.previousIndex];
            this.move(item, event.container, event.previousContainer, event.currentIndex);
        }
    }

    move(item, container, previousContainer, dropped_index?, skipCheckBatch?: boolean): void {
        const selecting: boolean = previousContainer.id === 'availableList';
        const previousIndex = previousContainer.data.indexOf(item);
        const index = (dropped_index || dropped_index === 0) ? dropped_index : (selecting ? 0 : container.data.length);

        transferArrayItem(
            previousContainer.data,
            container.data,
            previousIndex,
            index
        );

        if (selecting) {
            this.selectValue(item.value);
        } else {
            this.removeValue(item.value);
        }
        if (skipCheckBatch !== true) {
            this.checkBatch(previousContainer);
        }
    }

    moveColumns(move: 'select' | 'remove'): void {
        let currentData = [];
        let previousContainer, container
        if (move === 'select') {
            this.isSelecting = true;
            currentData = deepCopy(this.availableListBox.data);
            container = this.selectedListBox;
            previousContainer = this.availableListBox;
        } else {
            this.isRemoving = true;
            currentData = deepCopy(this.selectedListBox.data);
            container = this.availableListBox;
            previousContainer = this.selectedListBox;
        }
        if (move === 'remove' && this.removeItemDisabled) {
            currentData = currentData.filter(item => !this.removeItemDisabled(item.value));
        }
        this.changeDetection.detectChanges();
        currentData.forEach(item => {
            this.move(item, container, previousContainer, undefined, true);
        })

        this.checkBatch(previousContainer);
        this.isSelecting = false;
        this.isRemoving = false;
    }

    removeValue(value): void {
        this.value = this.value?.filter(item => this.valueFilter(item, value));
        this.items_removed.push(value);
        this.itemsRemoved.next(this.items_removed);
        this.items_added = this.items_added.filter(item => this.valueFilter(item, value));
        this.updateRemovedSelected();
        this.sapi.availableTotal += 1;
        this.sapi.selectedTotal -= 1;
        this.selectChange.emit(deepCopy(this.value));
    }

    selectValue(value): void {
        if (!this.value) {
            this.value = [];
        }
        this.value.push(value);
        this.items_added.push(value);
        this.itemsAdded.next(this.items_added);
        this.items_removed = this.items_removed.filter(item => this.valueFilter(item, value));
        this.updateRemovedSelected();
        this.sapi.availableTotal -= 1;
        this.sapi.selectedTotal += 1;
        this.selectChange.emit(this.value);
    }

    updateRemovedSelected(): void {
        this.sapi.itemsAdded.next(this.items_added);
        this.sapi.itemsRemoved.next(this.items_removed);
        this.setSelectTriggerText();
    }

    valueFilter(item, value): boolean {
        // Please leave excluded type equivalent here (==) since values can be int or string
        return item.id != value.id && item != value.id;
    }

    checkBatch(previousContainer): void {
        /**Make sure the next batch loads if user selects enough items to clear scroll bar**/
        const container_height = previousContainer.element.nativeElement.closest('.select-many-list-container').clientHeight;
        if ((previousContainer.data.length * this.sapi.item_height) > container_height) {
            return;
        }
        if (previousContainer.id === 'availableList') {
            this.sapi.resetList();
            this.getNextAvailable();
        } else {
            this.sapi.resetSelectedList();
            this.getNextSelected();
        }
    }

    onAccountsChanged($event) {
        this.accountsChange.emit($event);
        this.filterOptions(this.searchText);
    }

    clearSearch(): void {
        this.filterOptions("");
    }

    setSelectTriggerText(): void {
        if (!this.value?.length) {
            this.selectedTriggerText = this.value?.length === 0 ? this.disabled ? '' : this.placeholderText : 'Open to view selected';
            return;
        }

        const first = this.value[0];
        const id = first.id || first;
        let firstValue = this.sapi.stringFunction?.(first) || ((isGuid(id) || _isInteger(id)) ? this.idNameMap?.[id] : null);
        if (firstValue) {
            if (this.value.length === 1) {
                this.selectedTriggerText = firstValue;
            } else {
                this.selectedTriggerText = `${firstValue} (+${this.value.length - 1})`;
            }
            return;
        }

        this._fetchName(id);
    }

    private _fetchName(id) {
        this.api[this.apiModel].getName(id).pipe(first(), takeUntilDestroyed(this.destroyRef), tap((name: string) => {
            this.idNameMap[id] = name;
            if (name) {
                this.selectedTriggerText = this.value.length === 1 ? name : `${name} (+${this.value.length - 1})`;
            } else {
                this.selectedTriggerText = this.value.length === 1 ? '(1 selected)' : `(${this.value.length} selected)`;
            }
            this.changeDetection.markForCheck();
        })).subscribe();
    }

    removeAllDisabled(): boolean {
        return this.disabled || this.disable_items || this.sapi.loadingAvailable || this.sapi.loadingSelected
            || !this.selectedItems?.length || (this.removeItemDisabled && this.selectedItems.every(i => this.removeItemDisabled(i.value)));
    }

    selectAllDisabled(): boolean {
        return this.disabled || this.disable_items || this.sapi.loadingAvailable || this.sapi.loadingSelected || !this.availableItems?.length;
    }
}
